import { format, parse as parseDate } from 'date-fns';
import React from 'react';
import { Pressable } from 'react-native';

import { Order } from '@lib/entities';
import { useBreakpoint } from '@lib/hooks/useBreakpoints';
import useItemDisplay from '@lib/hooks/useItemDisplay';

import Image from '@components/Image';
import Stack from '@components/Stack';
import Typography from '@components/Typography';

import Button from './Button';
import SensitiveView from '@components/Inputs/TextField/SensitiveView';

interface PreviousOrderItemProps {
    order: Order;
    onPress?: () => void;
    reorderButtonTitle?: string;
    onReorder?: () => void;
    // Remove padding on the left / right
    snug?: boolean;
}

const PreviousOrderItem: React.FC<PreviousOrderItemProps> = (props: PreviousOrderItemProps) => {
    const breakpoint = useBreakpoint();
    const { formatTitle } = useItemDisplay();
    const { snug, order, onPress = () => {}, reorderButtonTitle, onReorder = () => {} } = props;
    const orderTimestamp = parseDate(order.order_timestamp, 'MM/dd/y HH:mm:ss', new Date());
    const isPickup = order.delivery_type === 'P';

    const line1 = `${format(orderTimestamp, 'MM/dd/yy')} - ${isPickup ? 'Pickup' : 'Delivery'}: ${
        isPickup ? order.store_name : order.delivery_address_name || ''
    }`;

    let line2 = '';
    order?.items?.forEach((item, idx) => {
        const itemName = formatTitle(item.menu_item_name);
        const isCustomized = item.customizations && item.customizations.length > 0;
        const label = isCustomized ? `${itemName} (customized)` : itemName;
        if (idx !== order.items.length - 1) {
            line2 += `${label}, `;
            return;
        }
        line2 += label;
    });
    const line3 = `$${order.total.toFixed(2)}`;

    const itemCount = order?.items?.length ?? 0;
    if (itemCount === 0) {
        return null;
    }

    return (
        <Stack flexDirection={'row'} alignItems={'center'} sx={{ width: '100%', maxWidth: '100%' }}>
            <Stack
                flexDirection="row"
                alignItems="center"
                sx={{
                    h: 106,
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    flex: 1,
                    shrink: 1
                }}
            >
                <Pressable
                    accessibilityRole="button"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        flexDirection: 'row',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}
                    onPress={onPress}
                >
                    <Stack
                        sx={{
                            sm: { mr: 16, ml: snug ? 0 : 24, w: 70, h: 70 },
                            md: { mr: 16, ml: snug ? 0 : 24, w: 88, h: 88 }
                        }}
                    >
                        <Image
                            alt={order.items[0].menu_item_name}
                            source={{ uri: order.items[0].image_url }}
                            contentFit="contain"
                            width={'100%'}
                            height={'100%'}
                            cx={{ height: 100, crop: 'fit' }}
                        />
                    </Stack>
                    <SensitiveView sensitive={true}>
                        <Stack
                            sx={{
                                sm: {
                                    w: 241,
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start'
                                },
                                md: {
                                    flex: 1,
                                    paddingRight: snug ? 0 : 32,
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }
                            }}
                            direction={{ sm: 'column', md: 'row' }}
                            nativeID="order-address-info"
                        >
                            <Stack sx={{ maxWidth: '100%', flexShrink: 1 }}>
                                <Typography
                                    color={'primary.dark'}
                                    semibold
                                    variant={'p3'}
                                    numberOfLines={1}
                                >
                                    {line1 + ''}
                                </Typography>
                                <Typography color={'primary.dark'} variant={'p3'} numberOfLines={1}>
                                    {line2}
                                </Typography>
                                <Typography color={'grey.dark'} variant={'p5'}>
                                    {line3}
                                </Typography>
                            </Stack>
                        </Stack>
                    </SensitiveView>
                </Pressable>
            </Stack>

            {reorderButtonTitle && breakpoint !== 'sm' && (
                <Stack
                    sx={{
                        sm: {
                            height: 30,
                            width: 50,
                            justifyContent: 'center',
                            marginVertical: 5
                        },
                        md: {
                            height: 'auto',
                            width: 'auto',
                            marginVertical: 0,
                            marginHorizontal: 32
                        }
                    }}
                >
                    <Button variant="primary.small" onPress={onReorder} tx={{ variant: 't6' }}>
                        {reorderButtonTitle}
                    </Button>
                </Stack>
            )}
        </Stack>
    );
};

export default PreviousOrderItem;
